import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { DateTime } from 'luxon'

import SimpleActionPopup from './SimpleActionPopup'
import BasicTable from './BasicTable'
import FormInput from './FormInput'
import ProgressBar from './ProgressBar'
import useDimensions from '../hooks/useDimensions'
import { errorToast, successToast } from '../helpers/ToastHelper'
import {
  getAddons,
  getUserAddonSubscriptions,
  grantAddon,
  cancelAddonSubscription
} from '../services/utils'

import '../styles/components/SubscriptionHistoryPopup.css'

const ADDON_KEY = 'mosty_ia'
const OPEN_STATUSES = ['pendingPayment', 'active', 'paymentFailed']

const STATUS_LABELS = {
  active: 'Ativo',
  pendingPayment: 'Aguardando pagamento',
  paymentFailed: 'Pagamento recusado',
  cancelled: 'Cancelado',
  'non-existent': 'Novo'
}

// Same palette as the legends of the plan history popup
const STATUS_COLORS = {
  active: '#D1F1BF',
  pendingPayment: '#FFF9C2',
  paymentFailed: '#FFC3C3',
  cancelled: '#FFC3C3'
}

const CHANGE_LABELS = {
  newAddonSubscriptionCreated: 'Contratado pela consultora',
  chargeConfirmedPaid: 'Pagamento confirmado',
  chargeConfirmedCanceled: 'Pagamento recusado',
  consultantCancelled: 'Cancelado pela consultora',
  cascadeCancelForUser: 'Cancelado junto com a conta',
  adminGranted: 'Liberado pelo admin',
  adminRecharged: 'Recarregado pelo admin',
  adminCancelled: 'Revogado pelo admin',
  freeTrialGrantedToAiPilot: 'Liberado (piloto de IA)'
}

const TRIGGER_LABELS = {
  purchase: 'Compra',
  manualRecharge: 'Recarga manual',
  autoRenew: 'Renovação automática'
}

const CHARGE_STATUS_LABELS = {
  pending: 'Pendente',
  paid: 'Paga',
  canceled: 'Cancelada'
}

const subscriptionsHeaders = [
  'Id',
  'Status',
  'Origem',
  'Saldo',
  'Criada em',
  'Última ação'
]
const subscriptionsKeys = [
  { key: 'id' },
  { key: 'status' },
  { key: 'origin' },
  { key: 'balance' },
  { key: 'createdAt' },
  { key: 'lastAction' }
]

const chargesHeaders = ['Id', 'Tipo', 'Valor', 'Status', 'Créditos', 'Data']
const chargesKeys = [
  { key: 'id' },
  { key: 'type' },
  { key: 'price' },
  { key: 'status' },
  { key: 'credits' },
  { key: 'createdAt' }
]

const eventsHeaders = ['Data', 'Evento', 'Status', 'Responsável']
const eventsKeys = [
  { key: 'createdAt' },
  { key: 'event' },
  { key: 'status' },
  { key: 'responsible' }
]

// Card ids are numeric. Anything else ('manual', trial placeholder) = no card.
const hasCard = addonSubscription =>
  /^\d+$/.test(String(addonSubscription.paymentMethodIdentifier))

const formatDate = date =>
  date ? DateTime.fromISO(date).toFormat('dd/MM/yyyy HH:mm') : '-'

const formatMoney = value =>
  `R$ ${Number(value)
    .toFixed(2)
    .replace('.', ',')}`

const getLastChange = addonSubscription => {
  const history = addonSubscription.addonSubscriptionHistory || []
  if (history.length === 0) return null
  return history.reduce((a, b) => (b.id > a.id ? b : a))
}

const getResponsible = change => {
  if (change.changeCode.startsWith('admin')) {
    return change.userId ? `Admin #${change.userId}` : 'Admin'
  }
  if (change.changeCode === 'freeTrialGrantedToAiPilot') return 'Equipe Mosty'
  if (
    change.changeCode === 'newAddonSubscriptionCreated' ||
    change.changeCode === 'consultantCancelled'
  ) {
    return 'Consultora'
  }
  return 'Sistema'
}

const Badge = ({ status }) => (
  <span
    className='small-paragraph'
    style={{
      display: 'inline-block',
      padding: '2px 12px',
      borderRadius: 20,
      background: STATUS_COLORS[status] || '#E5E5E5',
      fontWeight: 500
    }}
  >
    {STATUS_LABELS[status] || status}
  </span>
)

const StatCard = ({ title, children }) => (
  <div
    className='bg-white'
    style={{
      borderRadius: 12,
      padding: '14px 16px',
      boxShadow: '-1px 2px 4px rgba(0, 0, 0, 0.15)'
    }}
  >
    <p className='small-paragraph' style={{ color: 'var(--base-5)' }}>
      {title}
    </p>
    <div className='mt-2'>{children}</div>
  </div>
)

const creditsBarColor = (remaining, total) => {
  if (!remaining) return '#e0e0e0'
  const ratio = remaining / total
  if (ratio > 0.5) return '#8bc730'
  if (ratio > 0.2) return '#d3cd31'
  return '#ad3b65'
}

const MostyIaPopup = ({
  userId,
  userName,
  hasActivePlan,
  onChange,
  setShowPopup,
  token
}) => {
  const [loading, setLoading] = useState(true)
  const [addon, setAddon] = useState(null)
  const [addonSubscriptions, setAddonSubscriptions] = useState([])
  const [imageCredits, setImageCredits] = useState('')
  const [audioCredits, setAudioCredits] = useState('')
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [selectedMenu, setSelectedMenu] = useState('manage')

  const [paginatedSubscriptions, setPaginatedSubscriptions] = useState([])
  const [paginatedCharges, setPaginatedCharges] = useState([])
  const [paginatedEvents, setPaginatedEvents] = useState([])

  const { dimensions } = useDimensions()

  const current = addonSubscriptions.find(item =>
    OPEN_STATUSES.includes(item.status)
  )

  const fetchData = useCallback(async () => {
    try {
      const [addons, subscriptions] = await Promise.all([
        getAddons(token),
        getUserAddonSubscriptions(token, userId)
      ])
      const mostyIa = addons.find(item => item.key === ADDON_KEY) || null
      setAddon(mostyIa)
      setAddonSubscriptions(subscriptions)
      setImageCredits(mostyIa ? String(mostyIa.imageCreditsPerCycle) : '')
      setAudioCredits(mostyIa ? String(mostyIa.audioCreditsPerCycle) : '')
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [token, userId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const subscriptionsRows = useMemo(
    () =>
      addonSubscriptions.map(item => {
        const lastChange = getLastChange(item)
        return {
          id: item.id,
          statusKey: item.status,
          status: <Badge status={item.status} />,
          origin: hasCard(item) ? 'Cartão' : 'Liberação manual',
          balance: `${item.imageCreditsRemaining} img · ${item.audioCreditsRemaining} áud`,
          createdAt: formatDate(item.createdAt),
          lastAction: lastChange
            ? CHANGE_LABELS[lastChange.changeCode] || lastChange.changeCode
            : '-'
        }
      }),
    [addonSubscriptions]
  )

  const chargesRows = useMemo(
    () =>
      addonSubscriptions
        .flatMap(item => item.charges || [])
        .sort((a, b) => b.id - a.id)
        .map(charge => ({
          id: charge.id,
          type: TRIGGER_LABELS[charge.triggerType] || charge.triggerType,
          price: formatMoney(charge.price),
          status: CHARGE_STATUS_LABELS[charge.status] || charge.status,
          credits: `${charge.imageCreditsGranted} img · ${charge.audioCreditsGranted} áud`,
          createdAt: formatDate(charge.createdAt)
        })),
    [addonSubscriptions]
  )

  const eventsRows = useMemo(
    () =>
      addonSubscriptions
        .flatMap(item => item.addonSubscriptionHistory || [])
        .sort((a, b) => b.id - a.id)
        .map(change => ({
          id: change.id,
          createdAt: formatDate(change.createdAt),
          event: CHANGE_LABELS[change.changeCode] || change.changeCode,
          status: `${STATUS_LABELS[change.previousStatus] ||
            change.previousStatus} → ${STATUS_LABELS[change.newStatus] ||
            change.newStatus}`,
          responsible: getResponsible(change)
        })),
    [addonSubscriptions]
  )

  const subscriptionRowStyle = row => ({
    backgroundColor: STATUS_COLORS[row.statusKey]
  })

  const parseCredits = (value, label) => {
    if (value === '') return undefined
    const number = Number(value)
    if (!Number.isInteger(number) || number < 0 || number > 100000) {
      errorToast(`${label}: informe um número inteiro entre 0 e 100000.`)
      return null
    }
    return number
  }

  const handleGrant = async () => {
    const images = parseCredits(imageCredits, 'Imagens')
    const audios = parseCredits(audioCredits, 'Áudios')
    if (images === null || audios === null) return

    setLoading(true)
    try {
      await grantAddon(token, userId, {
        addonKey: ADDON_KEY,
        imageCredits: images,
        audioCredits: audios
      })
      successToast(
        current ? 'Créditos definidos com sucesso!' : 'Mosty IA liberado!'
      )
      await fetchData()
      onChange()
    } catch (err) {
      console.error(err)
      setLoading(false)
    }
  }

  const handleRevoke = async () => {
    setLoading(true)
    setConfirmRevoke(false)
    try {
      await cancelAddonSubscription(token, current.id)
      successToast('Acesso ao Mosty IA revogado.')
      await fetchData()
      onChange()
    } catch (err) {
      console.error(err)
      setLoading(false)
    }
  }

  const useCycleDefaults = () => {
    setImageCredits(String(addon.imageCreditsPerCycle))
    setAudioCredits(String(addon.audioCreditsPerCycle))
  }

  const renderCreditMeter = (remaining, cycleTotal) => {
    const total = Math.max(cycleTotal, remaining, 1)
    return (
      <div>
        <p style={{ fontSize: 26, fontWeight: 500, lineHeight: 1 }}>
          {remaining}
        </p>
        <div className='mt-3' style={{ marginBottom: -16 }}>
          <ProgressBar
            bgcolor={creditsBarColor(remaining, total)}
            count={remaining}
            total={total}
          />
        </div>
      </div>
    )
  }

  const renderStatCards = () => (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 mb-6'>
      <StatCard title='Status'>
        {current ? (
          <Badge status={current.status} />
        ) : (
          <span className='small-paragraph'>
            <b>Sem acesso</b> ao Mosty IA
          </span>
        )}
      </StatCard>
      <StatCard title='Origem'>
        <p className='small-paragraph'>
          {!current
            ? '-'
            : hasCard(current)
            ? `Cartão · renovação automática ${
                current.autoRenew ? 'ligada' : 'desligada'
              }`
            : 'Liberação manual · sem cartão, sem renovação'}
        </p>
      </StatCard>
      <StatCard title='Imagens restantes'>
        {current ? (
          renderCreditMeter(
            current.imageCreditsRemaining,
            addon.imageCreditsPerCycle
          )
        ) : (
          <p style={{ fontSize: 26, fontWeight: 500 }}>0</p>
        )}
      </StatCard>
      <StatCard title='Áudios restantes'>
        {current ? (
          renderCreditMeter(
            current.audioCreditsRemaining,
            addon.audioCreditsPerCycle
          )
        ) : (
          <p style={{ fontSize: 26, fontWeight: 500 }}>0</p>
        )}
      </StatCard>
    </div>
  )

  const renderNotes = () => (
    <>
      {!hasActivePlan && (
        <div
          className='mb-4 p-4 small-paragraph'
          style={{ background: '#FFF9C2', borderRadius: 12 }}
        >
          <b>Atenção:</b> esta consultora não tem um plano Mosty ativo no
          momento. O Mosty IA funciona em cima de um plano, mas você pode
          liberar mesmo assim.
        </div>
      )}
      {current && hasCard(current) && (
        <div
          className='mb-4 p-4 small-paragraph'
          style={{ background: '#DDE6FF', borderRadius: 12 }}
        >
          Esta consultora <b>paga com cartão</b>. Recarregar por aqui só define
          o saldo, <b>não gera cobrança</b> e não altera o cartão nem a
          renovação automática.
        </div>
      )}
    </>
  )

  const renderForm = () => (
    <div>
      <h4>{current ? 'Recarregar créditos' : 'Liberar Mosty IA'}</h4>
      <p className='small-paragraph mt-2 mb-4'>
        {current
          ? 'Define o novo saldo da consultora (substitui o saldo atual). Não gera cobrança.'
          : 'Libera sem cobrança e sem renovação automática, com o saldo abaixo.'}
      </p>
      <div className='flex gap-4'>
        <FormInput
          className='flex-1'
          type='number'
          label='Imagens'
          placeholder={`Padrão: ${addon.imageCreditsPerCycle}`}
          value={imageCredits}
          onChange={e => setImageCredits(e.target.value)}
        />
        <FormInput
          className='flex-1'
          type='number'
          label='Áudios'
          placeholder={`Padrão: ${addon.audioCreditsPerCycle}`}
          value={audioCredits}
          onChange={e => setAudioCredits(e.target.value)}
        />
      </div>
      <p
        className='small-paragraph underline cursor-pointer mt-3'
        onClick={useCycleDefaults}
      >
        Usar o padrão do ciclo ({addon.imageCreditsPerCycle} imagens ·{' '}
        {addon.audioCreditsPerCycle} áudios)
      </p>
    </div>
  )

  const renderManage = () => (
    <div>
      {renderNotes()}
      <h4>Situação atual</h4>
      {renderStatCards()}
      {renderForm()}
    </div>
  )

  const renderTableSection = ({
    title,
    emptyText,
    rows,
    headers,
    keys,
    paginated,
    setPaginated,
    itemsPerPage,
    rowStyle
  }) => (
    <div className='mt-6'>
      <h4>{title}</h4>
      {rows.length === 0 ? (
        <p className='small-paragraph mt-2'>{emptyText}</p>
      ) : (
        <BasicTable
          itemsPerPage={itemsPerPage}
          tableHeaders={headers}
          tableKeys={keys}
          filteredData={rows}
          paginatedData={paginated}
          setPaginatedData={setPaginated}
          rowStyle={rowStyle}
        />
      )}
    </div>
  )

  const renderHistory = () => (
    <div>
      {renderTableSection({
        title: 'Assinaturas do Mosty IA',
        emptyText: 'Esta consultora nunca teve o Mosty IA.',
        rows: subscriptionsRows,
        headers: subscriptionsHeaders,
        keys: subscriptionsKeys,
        paginated: paginatedSubscriptions,
        setPaginated: setPaginatedSubscriptions,
        itemsPerPage: 4,
        rowStyle: subscriptionRowStyle
      })}
      {renderTableSection({
        title: 'Cobranças',
        emptyText:
          'Nenhuma cobrança registrada (liberações manuais não geram cobrança).',
        rows: chargesRows,
        headers: chargesHeaders,
        keys: chargesKeys,
        paginated: paginatedCharges,
        setPaginated: setPaginatedCharges,
        itemsPerPage: 4
      })}
      {renderTableSection({
        title: 'Eventos (auditoria)',
        emptyText: 'Nenhum evento registrado.',
        rows: eventsRows,
        headers: eventsHeaders,
        keys: eventsKeys,
        paginated: paginatedEvents,
        setPaginated: setPaginatedEvents,
        itemsPerPage: 5
      })}
    </div>
  )

  const renderMenu = () => (
    <div className='subscription-popup-menu-options mb-4'>
      <div
        onClick={() => setSelectedMenu('manage')}
        className={`subscription-popup-menu-option${
          selectedMenu === 'manage' ? ' selected' : ''
        }`}
      >
        Gerenciar
      </div>
      <div
        onClick={() => setSelectedMenu('history')}
        className={`subscription-popup-menu-option${
          selectedMenu === 'history' ? ' selected' : ''
        }`}
      >
        Histórico
      </div>
    </div>
  )

  const popupContent = () => {
    if (confirmRevoke) {
      return (
        <div className='max-w-[670px] m-auto text-center'>
          <p className='big-paragraph'>
            Revogar o acesso de <b>{userName}</b> ao Mosty IA?
          </p>
          <p className='small-paragraph mt-4'>
            O saldo de créditos será <b>zerado</b> e ela deixa de conseguir usar
            a IA imediatamente.
          </p>
        </div>
      )
    }

    if (!addon) {
      return (
        <p className='small-paragraph text-center'>
          O Mosty IA não está cadastrado (ou está desativado) neste ambiente.
        </p>
      )
    }

    return (
      <div className='m-auto' style={{ maxWidth: 760 }}>
        {renderMenu()}
        {selectedMenu === 'manage' ? renderManage() : renderHistory()}
      </div>
    )
  }

  const footerProps = confirmRevoke
    ? {
        firstActionButtonText: 'CONFIRMAR REVOGAÇÃO',
        onPressFirstActionButton: handleRevoke,
        secondActionButtonText: 'VOLTAR',
        onPressSecondActionButton: () => setConfirmRevoke(false)
      }
    : selectedMenu === 'manage'
    ? {
        firstActionButtonText: current ? 'REVOGAR ACESSO' : undefined,
        onPressFirstActionButton: () => setConfirmRevoke(true),
        secondActionButtonText: current ? 'RECARREGAR' : 'LIBERAR',
        onPressSecondActionButton: handleGrant
      }
    : {}

  return (
    <SimpleActionPopup
      title={`Mosty IA - ${userName}`}
      content={popupContent()}
      onClosePopup={() => setShowPopup(false)}
      popupLoading={loading}
      containerStyle={{
        marginTop: 20,
        maxHeight: dimensions.innerHeight - 20 - 20,
        overflow: 'auto'
      }}
      {...(addon ? footerProps : {})}
    />
  )
}

const mapStateToProps = state => {
  const { user } = state
  return {
    token: user.token
  }
}

export default connect(mapStateToProps)(MostyIaPopup)
